import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UsersThree, Buildings, MapPin, ArrowUpRight, Users, Bell, Trash,
  Broadcast, Target, ChartLineUp, UserCircle,
} from "@phosphor-icons/react";

const STATUSES = [
  { v: "assigned", l: "Positionné", color: "#00E5FF" },
  { v: "contacted", l: "Contacté", color: "#A855F7" },
  { v: "discussion", l: "En discussion", color: "#F59E0B" },
  { v: "won", l: "Gagné", color: "#22C55E" },
  { v: "lost", l: "Perdu", color: "#EF4444" },
];
const statusMeta = (v) => STATUSES.find((s) => s.v === v) || STATUSES[0];
const availLabel = (v) => ({ available: "Disponible", soon: "Bientôt dispo", unavailable: "En mission" }[v] || "Disponible");

export default function Tracking() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("positionings");
  const [data, setData] = useState({ consultants: [], total: 0 });
  const [watched, setWatched] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: w }] = await Promise.all([
        api.get("/positionings"),
        api.get("/watched-companies"),
      ]);
      setData(p);
      setWatched(w);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (assignmentId, status) => {
    // optimistic update
    setData((d) => ({
      ...d,
      consultants: d.consultants.map((c) => ({
        ...c,
        positionings: c.positionings.map((p) => p.assignment_id === assignmentId ? { ...p, status } : p),
      })),
    }));
    try {
      await api.patch(`/assignments/${assignmentId}`, { status });
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    }
  };

  const unwatch = async (id) => {
    try {
      await api.delete(`/watched-companies/${id}`);
      setWatched((w) => w.filter((x) => x.id !== id));
      toast.success("Entreprise retirée du suivi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="tracking-page">
      <div className="mb-6">
        <div className="overline mb-2">Pilotage commercial</div>
        <h1 className="font-display text-3xl font-bold">Suivi</h1>
        <p className="text-muted text-sm mt-1">Suivez vos consultants positionnés et les entreprises que vous ciblez.</p>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-border">
        <TabBtn active={tab === "positionings"} onClick={() => setTab("positionings")} icon={ChartLineUp} testid="tab-positionings">
          Positionnements <span className="text-muted font-mono">({data.total})</span>
        </TabBtn>
        <TabBtn active={tab === "watched"} onClick={() => setTab("watched")} icon={Bell} testid="tab-watched">
          Comptes suivis <span className="text-muted font-mono">({watched.length})</span>
        </TabBtn>
      </div>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : tab === "positionings" ? (
        data.consultants.length === 0 ? (
          <EmptyState icon={UsersThree} title="Aucun positionnement" testid="positionings-empty"
            text="Positionnez un consultant sur une opportunité depuis sa fiche pour la suivre ici.">
            <button onClick={() => navigate("/app")} className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift">Rechercher des opportunités</button>
          </EmptyState>
        ) : (
          <div className="space-y-6" data-testid="positionings-list">
            {data.consultants.map((c) => (
              <div key={c.consultant.id} className="surface rounded-lg p-5" data-testid={`positioning-consultant-${c.consultant.id}`}>
                <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center bg-accent/10 border border-accent/30 shrink-0">
                    <UserCircle size={20} weight="duotone" className="text-accent" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-fg truncate">{c.consultant.name}</div>
                    <div className="text-xs text-muted truncate">{c.consultant.role || "—"} · {c.consultant.seniority} · {availLabel(c.consultant.availability)}</div>
                  </div>
                  <span className="ml-auto text-xs font-mono text-muted shrink-0">{c.positionings.length} mission{c.positionings.length > 1 ? "s" : ""}</span>
                </div>
                <div className="space-y-3">
                  {c.positionings.map((p) => {
                    const sm = statusMeta(p.status);
                    return (
                      <div key={p.assignment_id} className="flex flex-wrap items-center gap-3 bg-surface-2 border border-border rounded-md p-3" data-testid={`positioning-${p.assignment_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-fg text-sm truncate">{p.role_title}</div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted font-mono mt-0.5">
                            <span className="flex items-center gap-1"><Buildings size={12} /> {p.company_name}</span>
                            {p.city && <span className="flex items-center gap-1"><MapPin size={12} /> {p.city}</span>}
                            <Link to={`/app/company/${p.company_id}`} className="flex items-center gap-1 text-accent hover:underline" data-testid={`positioning-decideurs-${p.assignment_id}`}>
                              <Users size={12} /> {p.contacts_count} décideur{p.contacts_count > 1 ? "s" : ""}
                            </Link>
                          </div>
                        </div>
                        <Select value={p.status} onValueChange={(v) => changeStatus(p.assignment_id, v)}>
                          <SelectTrigger className="w-40 h-8 bg-surface border-border text-xs" data-testid={`positioning-status-${p.assignment_id}`}>
                            <span className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: sm.color }} />
                              <SelectValue />
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button onClick={() => navigate(`/app/opportunity/${p.signal_id}`)} className="flex items-center gap-1 text-xs text-fg hover:text-accent shrink-0" data-testid={`positioning-open-${p.assignment_id}`}>
                          Ouvrir <ArrowUpRight size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        watched.length === 0 ? (
          <EmptyState icon={Bell} title="Aucun compte suivi" testid="watched-empty"
            text="Suivez une entreprise depuis sa fiche pour être alerté dès qu'elle publie un nouveau besoin IT correspondant à un consultant disponible.">
            <button onClick={() => navigate("/app")} className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift">Explorer les entreprises</button>
          </EmptyState>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="watched-list">
            {watched.map((w) => (
              <div key={w.id} className="surface rounded-lg p-5 hover-lift" data-testid={`watched-${w.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/app/company/${w.company_id}`} className="min-w-0 group">
                    <div className="font-display font-bold text-fg truncate group-hover:text-accent transition-colors">{w.company_name}</div>
                    <div className="text-xs text-muted truncate">{w.sector || "Entreprise"}{w.city ? ` · ${w.city}` : ""}</div>
                  </Link>
                  <button onClick={() => unwatch(w.id)} className="p-1.5 rounded-md text-muted hover:text-destructive shrink-0" data-testid={`unwatch-${w.id}`} title="Ne plus suivre">
                    <Trash size={15} />
                  </button>
                </div>
                <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border text-xs font-mono">
                  <span className="flex items-center gap-1 text-muted"><Broadcast size={13} /> {w.active_signals} besoin{w.active_signals > 1 ? "s" : ""}</span>
                  {w.new_signals > 0 && (
                    <span className="flex items-center gap-1 text-accent" data-testid={`watched-new-${w.id}`}>
                      <Target size={13} weight="fill" /> {w.new_signals} nouveau{w.new_signals > 1 ? "x" : ""}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${active ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg"}`}>
      <Icon size={16} weight={active ? "fill" : "regular"} /> {children}
    </button>
  );
}

function EmptyState({ icon: Icon, title, text, children, testid }) {
  return (
    <div className="surface rounded-lg p-12 text-center" data-testid={testid}>
      <Icon size={40} weight="duotone" className="text-accent mx-auto mb-3" />
      <div className="font-display font-bold text-fg mb-1">{title}</div>
      <p className="text-muted text-sm mb-5 max-w-md mx-auto">{text}</p>
      {children}
    </div>
  );
}
